// The organisation the installation owner works in, one per browser tab.
// Org admins and staff never set it: their organisation is implied by the session.
import type { Organisation } from './types'

const KEY = 'qms.organisation'

// A new tab starts from the owner's last choice in any tab, then keeps its own.
function load(): string {
  try {
    return window.sessionStorage.getItem(KEY) ?? window.localStorage.getItem(KEY) ?? ''
  } catch {
    return ''
  }
}

let current = typeof window === 'undefined' ? '' : load()
const listeners = new Set<() => void>()

/** The chosen organisation's id, or '' for none. */
export function contextOrganisation(): string {
  return current
}

export function setContextOrganisation(id: string): void {
  if (id === current) return
  current = id
  try {
    if (id) {
      window.sessionStorage.setItem(KEY, id)
      window.localStorage.setItem(KEY, id)
    } else window.sessionStorage.removeItem(KEY)
  } catch {
    // Kept in memory only.
  }
  for (const l of listeners) l()
}

export function onOrganisationChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Endpoints whose answer belongs to one organisation, outside Settings (which names it itself).
 *  /auth/me too: the owner's modules and options are the chosen organisation's. */
const SCOPED =
  /^\/(layouts|assets|signage|fields|break-reasons|counter-policy|report-views|report-schedules|audit|staff|roles|permissions|booking|appointments|modules|reports|auth\/me)(\/|\?|$)/

/** The path with ?organisation= added for an org-scoped endpoint, unless it already names one. */
export function scopedPath(path: string, organisation = current): string {
  if (!organisation || !SCOPED.test(path) || /[?&]organisation=/.test(path)) return path
  return `${path}${path.includes('?') ? '&' : '?'}organisation=${encodeURIComponent(organisation)}`
}

/** Pages that read one organisation's data; the owner chooses one before they open. */
const NEEDS_ORGANISATION = /^\/(counter|reception|floor|appointments|people|screens|signage|reports)(\/|$)/

export function pageNeedsOrganisation(path: string): boolean {
  return NEEDS_ORGANISATION.test(path)
}

export interface OrgChoice {
  /** The person may work in any organisation (the installation owner). */
  crosses: boolean
  /** Whether the person belongs to an organisation of their own. */
  member: boolean
  /** null until the list has answered. */
  organisations: readonly Pick<Organisation, 'id' | 'active'>[] | null
  chosen: string
}

/** Several organisations to choose between: the top bar offers the switcher. */
export function switchable(c: OrgChoice): boolean {
  return c.crosses && (c.organisations?.length ?? 0) > 1
}

/** An org-scoped page has nothing to read: an owner of several organisations has chosen none,
 *  or the list has not answered yet. With exactly one, the server takes it. */
export function organisationNeeded(c: OrgChoice): boolean {
  if (!c.crosses || c.member || c.chosen) return false
  if (c.organisations === null) return true
  return c.organisations.filter((o) => o.active !== false).length !== 1
}

/** The remembered choice, dropped when it names an organisation no longer listed. */
export function validChoice(chosen: string, organisations: readonly Pick<Organisation, 'id'>[]): string {
  if (chosen && organisations.some((o) => o.id === chosen)) return chosen
  // One organisation on the server: there is nothing to choose.
  return organisations.length === 1 ? organisations[0]!.id : ''
}
